import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import moment from "moment";
import { BsModalService } from "ngx-bootstrap/modal";
import { Lightbox, LightboxConfig } from "ngx-lightbox";
import { Subject, Subscription, asyncScheduler, debounceTime } from "rxjs";
import { ShowRequest } from "../../core";
import { AppState, DbEntityChange, DbEntityChangedEvent, sameRevision } from "../../core/app-state";
import { CorrelationRecord, MiscLeafNode, ScoutPath, ScoutPoint, ScoutPointPhoto, activity_status, isAndroid, isIos } from "../../core/models";
import { RoleMgr } from "../../core/role-mgr";
import { ScoutPointPhotoDoc } from "../../core/schema/scout-point-photo-schema";
import { ScoutTaskDoc } from "../../core/schema/scout-task-schema";
import { PostProcessingService, ScoutPointPhotoService } from "../../core/services";
import { ScoutPointPhotoEditorComponent } from "../../scout/scout-point/scout-point-photo-editor/scout-point-photo-editor.component";
import { TableViewContext } from "../../shared/components/table-view/table-view-component-base";
import { FesRutTestPointConfig } from "../models";
import { VolunteerTaskMapPoint } from "../models/volunteer-task-map-point";
import { VolunteerTaskPointViewRecord } from "../models/volunteer-task-point-record";
import { VolunteerTaskViewRecord } from "../models/volunteer-task-record";
import { VolunteerTaskPointViewService } from "../services/volunteer-task-point-view.service";
import { MobilePhotoContext, VolunteerTaskPointService } from "../services/volunteer-task-point.service";
import { VolunteerTaskMapComponent } from "../volunteer-task-map/volunteer-task-map.component";
import _ from "lodash";
import { InlineEdit, VolunteerTaskPointTestParamsComponent } from "./volunteer-task-point-test-params/volunteer-task-point-test-params.component";
import { CorrelationTableComponent } from "../../post-processing/correlation-table/correlation-table.component";
import { RutCallLogRecord } from "../../core/models/rut-call-log-record";
import { RutCallLogTableComponent } from "../../post-processing/rut-call-log/rut-call-log-table.component";
import { RutCallLogComponent } from "../../post-processing/rut-call-log/rut-call-log.component";

type ScoutInfo = {
  scout: ScoutTaskDoc;
  scoutPath: ScoutPath;
  scoutPoint: ScoutPoint;
};

@Component({
  selector: "app-volunteer-task-point",
  styleUrls: ["volunteer-task-point.component.scss"],
  templateUrl: "volunteer-task-point.component.html",
})
export class VolunteerTaskPointComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input('volunteerTaskPoint') set volunteerTaskPointSetter(vtp: VolunteerTaskPointViewRecord) {
    //console.log("volunteerTaskPointSetter", vtp);
    this.volunteerTaskPoint = vtp;
    this.resetInlineEdits();
    if (this.callLogVisible) {
      this.callLogVisible = false;
      asyncScheduler.schedule(() => this.callLogVisible = true)
    }
    this.initDbEntityChangeSubscription();
    this.rutCallLogContext = JSON.stringify(this.buildRutCallLogContext());
    this.correlationContext = JSON.stringify(this.buildCorrelationContext());
    this.onRefresh().catch(e => this.volunteerTaskPointService.publishError(e, this))
  }

  volunteerTaskPoint: VolunteerTaskPointViewRecord;

  @Input() task: VolunteerTaskViewRecord;

  @Input('scoutInfo') set scoutInfoSetter(si: ScoutInfo) {
    this.scoutInfo = si;
    this.initPhotosForPointQuery();
  }

  scoutInfo: ScoutInfo;

  @Input() mapPoint: VolunteerTaskMapPoint;

  @Input('mapPoints') set mapPointsSetter(mps: VolunteerTaskMapPoint[]) {
    this.mapPoints = [...mps]
    this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => mpt.miscChildren = []);
  }
  mapPoints: VolunteerTaskMapPoint[];

  @Input() config: FesRutTestPointConfig;

  @Output() pointSelected = new EventEmitter<VolunteerTaskMapPoint>();

  @Output() taskPointModified = new EventEmitter<VolunteerTaskPointViewRecord>();
  @ViewChild("taskMap") taskMap: VolunteerTaskMapComponent;
  @ViewChild("testParams") testParams: VolunteerTaskPointTestParamsComponent;
  @ViewChild("rutCallLog") rutCallLog: RutCallLogComponent;
  @ViewChild("correlationTable") correlationTable: CorrelationTableComponent;
  @ViewChildren(ScoutPointPhotoEditorComponent)
  photoEditors!: QueryList<ScoutPointPhotoEditorComponent>;

  photoDocs: ScoutPointPhotoDoc[] = [];
  callLogVisible = false;

  @Input() correlationVisible = false;

  rutCallLogContext: string;
  correlationContext: string;

  //Some misc volunteer point rec fields
  demoAttrDefs = [
    { attr: "buildingName", title: "Building Name" },
    { attr: "volunteerName", title: "Volunteer" },
    { attr: "volunteerEmail", title: "Email" },
    { attr: "pointId", title: "Point Id" },
    //{ attr: "latitude", title: "Latitude" },
    //{ attr: "longitude", title: "Longitude" },
    { attr: "floorAndDescript", title: "Floor Description" },
    //{ attr: "modifiedByName", title: "Modified By" },
    //{ attr: "modifiedAtText", title: "Modified At" },
  ];

  commonName: InlineEdit<string>;
  status: InlineEdit<activity_status.Id>;

  photosSub: Subscription;
  isLoadingPhotos = false;

  saveTaskPointErrorMsg = '';
  loading = false;


  constructor(
    protected appState: AppState,
    public roleMgr: RoleMgr,
    public photoService: ScoutPointPhotoService,
    public volunteerTaskPointService: VolunteerTaskPointService,
    public volunteerTaskPointViewService: VolunteerTaskPointViewService,
    public bsModalService: BsModalService,
    public postProcessingService: PostProcessingService,
    private lightbox: Lightbox,
    lightboxConfig: LightboxConfig,
  ) {
    lightboxConfig.fitImageInViewPort = true;
    lightboxConfig.showRotate = true;
    lightboxConfig.showZoom = true;
    lightboxConfig.fadeDuration = 0.4;
    this.debouncedRefresh.pipe(
      debounceTime(800) // just in case we get a lot of them (shouldn't)
    ).subscribe(bResetForm => {
        this.onRefresh(bResetForm).catch((e: any)=> {
          this.volunteerTaskPointService.publishError(e,this);
        });
    });
  }

  pointChangeSub?: Subscription;

  ngOnInit(): void {
    this.commonName = new InlineEdit<string>(this, 'commonName', 'custom test point name', '');
    this.commonName.save = async () => {
      this.commonName.errorMsg = undefined;
      return await this.volunteerTaskPointViewService.saveCommonName({
        volunteerTaskPointId: this.volunteerTaskPoint.id,
        commonName: this.commonName.value
      });
    }
    const titleCase = (v?:string) => v?.split(' ').map(v=>_.capitalize(v)).join(' ');
    this.status = new InlineEdit<activity_status.Id>(this, 'status', 'Test point status', this.volunteerTaskPoint?.status,
      () => `"${titleCase(activity_status.Names[this.status.value])}"`);
  }

  ngAfterViewInit(): void {
    this.taskMap.setSelectedPoint(this.mapPoint);
    this.initPhotosForPointQuery();
  }


  initDbEntityChangeSubscription = () => {
    this.pointChangeSub?.unsubscribe();
    this.pointChangeSub = this.appState.dbEntityChange$.subscribe({
      next: this.onEntityChanged
    });
  }

  onEntityChanged = (changeEvent: DbEntityChangedEvent) => {
    if ( changeEvent.channel === 'volunteer_point_change') {
      const change = changeEvent.payload as DbEntityChange & {commonName: string};
      const bSamePointId = change.id === this.volunteerTaskPoint.volunteerPointId;
      if ( bSamePointId ) {
        if ( change.commonName && change.commonName !== this.volunteerTaskPoint.commonName ) {
          this.volunteerTaskPoint.commonName = change.commonName;
          this.commonName.reset();
        }
        // potentially pointId could change too?  Assume just commonName for now
      }
    } else if ( changeEvent.channel === 'volunteer_task_point_change') {
      if ( changeEvent.payload.id === this.volunteerTaskPoint.id
          && !sameRevision(changeEvent, this.volunteerTaskPoint.revisionId) ) {
        this.debouncedRefresh.next(false);
      }
    } else if ( changeEvent.channel === 'volunteer_call_rut_change') {
      const rutChange = changeEvent.payload as DbEntityChange & {taskPointId: number};
      if ( rutChange.taskPointId === this.volunteerTaskPoint.id ) {
        this.appState.notifyInfo(`Volunteer call added or updated.`, this, 3);
        this.rutCallLog?.table.load().catch(
          e => this.volunteerTaskPointService.publishError(e, this)
        );
      }
    } else if ( changeEvent.channel === 'volunteer_call_photo_change') {
      const photoChange = changeEvent.payload as DbEntityChange & {taskPointId: number};
      if ( photoChange.taskPointId === this.volunteerTaskPoint.id ) {
        this.appState.notifyInfo(`Volunteer call photo added or updated.`, this, 3);
        this.rutCallLog?.table.load().catch(
          e => this.volunteerTaskPointService.publishError(e, this)
        );
      }
    }
  }

  ngOnDestroy(): void {
    this.photosSub?.unsubscribe();
    this.pointChangeSub?.unsubscribe();
  }

  initPhotosForPointQuery = () => {
    if (this.scoutInfo?.scoutPoint) {
      const queryScoutPointId = this.scoutInfo.scoutPoint.id;
      this.isLoadingPhotos = true;
      this.photosSub?.unsubscribe();
      let photoQuery = this.photoService.rxCollection
        .find({
          selector: {
            scoutPointId: queryScoutPointId
          }
        });
      this.photosSub = photoQuery.$.subscribe({
        next: (docs: ScoutPointPhotoDoc[]) => {
          if (this.scoutInfo?.scoutPoint?.id !== queryScoutPointId) {
            return;
          }
          this.isLoadingPhotos = false;
          this.initPhotosForPointType(docs);
        },
        error: (error) => {
          console.log("photoQuery subscription received error");
          this.isLoadingPhotos = false;
          this.photoService.publishError(error, this);
        }
      });
    }
  };

  initPhotosForPointType(photoDocs: ScoutPointPhotoDoc[]) {
    this.photoService.initPhotosForPointType(
      photoDocs,
      this.scoutInfo.scoutPoint.type,
      this.photoDocs
    );
  }

  buildRutCallLogContext(): Partial<TableViewContext> {
    return {
      filters: [
        {key:"taskPointId", value: `${this.volunteerTaskPoint?.id||''}`, mode:"="},
        //{key:"validTestCall", value: "true", mode:"="}
      ],
      visibleNames:[
        'dialedNum','localStartTime','duration','setup', 'traffic', 'validTestCall', 'photoCnt', 'comments'
      ],
      sorts:[
        {key:"startTime", order:"DESC"}
      ]
    }
  }

  buildCorrelationContext(): Partial<TableViewContext> {
    return {
      filters: [
        {
          key: 'stage',
          value: this.task.stage,
          mode: '='
        },
        {
           key: 'cycle',
           value: `${this.task.seriesId}-${this.task.seriesPosition}`,
           mode: '='
        },
        {
          key: 'pointid',
          value: this.volunteerTaskPoint.pointId,
          mode: '='
        },
        {
          key: 'correlatedcall',
          value: 'true',
          mode: '='
        },
        // {
        //   key: 'type',
        //   value: 'chosen',
        //   mode: '='
        // }
      ],
      visibleNames:[
        'callstarttime','correlatedcall','os','rantech','summarytech','type','horizontalerror','verticalerror','altitude'
      ],
      sorts:[
        {key:"callstarttime", order:"DESC"},
        {key:"horizontalerror", order:"ASC"},
      ]
    }
  }

  onPhotoClick = (photo: ScoutPointPhoto, photoUrl: string) => {
    const albums = this.photoEditors
      .filter((editor) => editor.photoUrl != null)
      .map((editor) => {
        return {
          src: editor.photoUrl,
          thumb: null,
          caption: editor.photo?.title + " (" + editor.photo?.fileName + ")",
          photo: editor.photo,
        };
      });
    const index = albums.findIndex((album) => album.photo == photo);
    this.lightbox.open(albums, index);
  };

  onMapPointSelected = (mpt: VolunteerTaskMapPoint) => {
    this.pointSelected.next(mpt);
  };

  onShowRutLog = () => {
    const queryForTaskPoint: Partial<TableViewContext> = {
      filters: [{
        key: 'taskPointId',
        value: `${this.volunteerTaskPoint.id}`,
        mode: '='
      }],
      sorts: [
        {
          key: 'startTime',
          order: 'DESC'
        }
      ]
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'rut call logs', JSON.stringify(queryForTaskPoint))
    );
  }

  toggleCorrelationVisible = () => {
    this.correlationVisible =!this.correlationVisible;
    if ( !this.correlationVisible) {
      this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => mpt.miscChildren = []);
      this.taskMap?.plotWhenReady(this.mapPoints);
    } else {
      // template will add table and trigger load
    }
  }

  onShowCorrelation = () => {
    const queryForTaskPoint: Partial<TableViewContext> = {
      filters: [
        {
          key: 'stage',
          value: this.task.stage,
          mode: '='
        },
        {
           key: 'cycle',
           value: `${this.task.seriesId}-${this.task.seriesPosition}`,
           mode: '='
        },
        {
          key: 'pointid',
          value: this.volunteerTaskPoint.pointId,
          mode: '='
        }
      ]
    };
    this.appState.showView.next(
      new ShowRequest('tab', 'correlation', JSON.stringify(queryForTaskPoint))
    );
  }

  bindMobilePhotoContextForTaskPoint = this.volunteerTaskPointService.bindMobilePhotoContextForTaskPoint;

  toggleMobilePhotoForTaskPoint = this.volunteerTaskPointService.toggleMobilePhotoForTaskPoint;

  contextByPhotoId: { [photoId: string]: MobilePhotoContext } = {};

  photosContextBound = (contextByPhotoId: { [photoId: string]: MobilePhotoContext }) => {
    //TODO - this just grabs reference of photo browsers context (should make a copy but then we have to update state on dbl clicks)
    this.contextByPhotoId = contextByPhotoId;
  }

  lastCallMsg: string;
  refreshing = false;

  debouncedRefresh = new Subject<boolean>();

  onRefresh = async (bResetForm: boolean=true) => {
    this.loading = true;
    this.refreshing = true;
    try {
      console.log(`onRefresh(bResetForm=${bResetForm})`);
      const vtp = this.volunteerTaskPoint;
      const taskPoint = await this.volunteerTaskPointViewService.getById(this.volunteerTaskPoint.id);
      if (vtp != this.volunteerTaskPoint) {
        return;
      }
      const buildLastCallMsg = () => 'Last modified ' + moment(taskPoint.lastCallTime).format('MMM D, YYYY, h:mm:ss A');
      //(moment.duration(Date.now()-Date.parse(taskPoint.lastCallTime),'ms')as any).format(
      //  'd [days][,] h [hours][,] m [minutes][, and] s [seconds]', { trim: 'both' });
      this.lastCallMsg = taskPoint.lastCallTime && buildLastCallMsg();
      this.saveTaskPointErrorMsg = '';
      if ( bResetForm) {
         this.resetInlineEdits();
      } else {
        this.updateInlineEdits(taskPoint);
      }
      console.log('onRefresh() taskPoint', taskPoint)
      this.taskPointModified.next(taskPoint);
      this.taskMap.setSelectedPoint(this.mapPoint);
      this.initPhotosForPointQuery();
      this.initDbEntityChangeSubscription();
    } catch (e: any) {
      console.error(e);
      this.volunteerTaskPointViewService.publishError(e, this, 30);
    } finally {
      this.loading = false;
      this.refreshing = false;
    }
  }

  async onSaveCommonName() {
    try {
      this.loading = true;
      const taskPoint = await this.commonName.save();
      this.appState.notifySuccess(`${this.commonName.title}: ${this.commonName.formattedValue()}`, this, 2);
      this.commonName.reset();
      this.taskPointModified.next(taskPoint);
    } catch (e: any) {
      console.error(e);
      const msg = this.volunteerTaskPointService.extractErrorMsg(e);
      this.appState.notifyError(msg, this, 2);
      this.commonName.errorMsg = `Error saving ${this.commonName.name} for point. ${msg}`;
    } finally {
      this.loading = false;
    }
  }

  async onSaveInlineEdit<T>(inlineEdit: InlineEdit<T>, eventTarget?: EventTarget) {
    await this.testParams.onSaveInlineEdit(inlineEdit, eventTarget);
  }

  async onDeleteTaskPoint() {
    const deletedTaskPoint = await this.volunteerTaskPointViewService.promptedDeleteTaskPoint(
      this.volunteerTaskPoint
    );
    await this.onRefresh();
    return deletedTaskPoint;
  }

  resetInlineEdits() {
    console.log(`resetInlineEdits() for ${this.volunteerTaskPoint.id}`);
    this.commonName?.reset();
    this.status?.reset();
    this.testParams?.resetInlineEdits();
  }

  updateInlineEdits(taskPoint: VolunteerTaskPointViewRecord) {
    console.log(`updateInlineEdits() for ${this.volunteerTaskPoint.id}`);
    this.status?.updateValue(taskPoint?.status);
    this.commonName?.updateValue(taskPoint?.commonName);
    //TODO... complete for the test parameter inputs
    //this.testParams?.updateInlineEdits(taskPoint);
  }

  onCorrelationRecordsLoaded = (rows: CorrelationRecord[]) => {
    this.mapPoints.forEach((mpt:VolunteerTaskMapPoint) => {
      const ptCorrelationRecs = rows.filter(cr=>cr.pointid == mpt.pointId);
      mpt.miscChildren = [];
      const children: MiscLeafNode[] = mpt.miscChildren;
      ptCorrelationRecs.forEach(cr=>{
        if ( cr.latitude != null && cr.longitude != null ) {
          children.push( {
            latitude: Number(cr.latitude),
            longitude: Number(cr.longitude),
            id: cr.viewrowid,
            parentId: mpt.id,
            title: `${cr.pointid} | ${cr.horizontalerror?.toFixed(2)}m | ${cr.carrier} | ${cr.type} | ${cr.summarytech}`,
            iconClass: isAndroid(cr.os) ? 'fab fa-android android-icon' : isIos(cr.os) ? 'fab fa-apple apple-icon' : undefined,
            type: 'call log',
            data: cr
          });
        }
      });
    });
    this.taskMap?.plotWhenReady(this.mapPoints);
  }

  correlationRowHover = (hoverEvt: { event:MouseEvent,record: CorrelationRecord}, active: boolean) => {
    //console.log('correlationRowHover()', hoverEvt,active);
    this.taskMap?.highlightLeafNode(hoverEvt.record.viewrowid, active);
    this.highlightRutRows(hoverEvt.record.callsourceid, active);
  }

  rutRowHover = (hoverEvt: { event:MouseEvent,record: RutCallLogRecord}, active: boolean) => {
    //console.log('rutRowHover()', hoverEvt,active);
    this.highlightCorrelationRows((rec)=>rec.callsourceid==hoverEvt.record.id, active);
    this.correlationTable?.records?.forEach( (rec) => {
      if ( rec.callsourceid==hoverEvt.record.id ) {
        this.taskMap?.highlightLeafNode(rec.viewrowid, active);
      }
    });
  }

  onMiscLeafNodeEvent = (evt: {srcEvent: MouseEvent, id : number, parentId: string}) => {
    //console.log('onMiscLeafNodeEvent()', evt, evt.srcEvent.type);
    const recIndex = this.correlationTable?.records.findIndex(cr=>cr.viewrowid == evt.id);
    if ( recIndex === undefined || recIndex === -1 ) {
      console.warn('onMiscLeafNodeEvent() no correlation record found for id', evt.id);
      return;
    }
    const corrRec = this.correlationTable.records[recIndex]
    if ( evt.srcEvent.type==='click' ) {
      this.correlationTable.showDetailModal(corrRec);
    } else if ( ['mouseover','mouseout'].includes(evt.srcEvent.type) ) {
      this.highlightCorrelationRows((rec)=>rec.viewrowid==evt.id, evt.srcEvent.type==='mouseover');
      this.highlightRutRows(corrRec.callsourceid,evt.srcEvent.type==='mouseover');
    }
  }

  highlightCorrelationRows(filterFn:(rec:CorrelationRecord)=>boolean, highlight: boolean) {
    if ( !this.correlationTable) {
      return;
    }
    if ( highlight) {
      const recIndices: number[] = [];
      this.correlationTable.records.forEach( (rec,i) => {
        if ( filterFn(rec) ) {
          recIndices.push(i);
        }
      });
      this.correlationTable.applySelectedIndices({
        selectedIndices:recIndices,
        scrollIntoViewMode:false//'nearest'
      });
    } else {
      this.correlationTable.applySelectedIndices({
        selectedIndices:[],
      });
    }
  }

  highlightRutRows(callSourceId: number, highlight: boolean) {
    const rutTable: RutCallLogTableComponent = this.rutCallLog?.table;
    if ( !rutTable) {
      return;
    }
    if ( highlight) {
      const rutRecIndices: number[] = [];
      rutTable.records.forEach( (rec,i) => {
        if ( rec.id == callSourceId) {
          rutRecIndices.push(i);
        }
      });
      rutTable.applySelectedIndices({
        selectedIndices:rutRecIndices,
        scrollIntoViewMode:false//'nearest'
      });
    } else {
      rutTable.applySelectedIndices({
        selectedIndices:[],
      });
    }
  }
}
